import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddComment,
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  LockOutlined as LockOutlinedIcon,
  MoreHorizOutlined as MoreHorizOutlinedIcon,
  SearchOutlined as SearchOutlinedIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { hasPermission } from "../../config/permissionUtils";
import {
  brandsService,
  categoriesService,
  familiesService,
  formatsService,
  productsService,
} from "../../services";
import { useAuth } from "../../context/AuthContext";
import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import {
  safeSessionStorageGetItem,
  safeSessionStorageRemoveItem,
  safeSessionStorageSetItem,
} from "../../utils/browserStorage";

const PRODUCT_FORM_DRAFT_STORAGE_PREFIX = "product-form-draft:";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toPositiveNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const asMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "";
  return parsed.toFixed(2);
};

const formatQtyLabel = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return Number.isInteger(parsed) ? String(parsed) : String(parsed);
};

const getMarginFromSale = (costPrice, salePrice) => {
  const cost = toPositiveNumber(costPrice);
  const sale = toPositiveNumber(salePrice);
  if (!cost || !sale) return "";
  const margin = ((sale - cost) / sale) * 100;
  if (!Number.isFinite(margin)) return "";
  return margin.toFixed(2);
};

const getSaleFromMargin = (costPrice, marginPct) => {
  const cost = toPositiveNumber(costPrice);
  const margin = Number(marginPct);
  if (!cost || !Number.isFinite(margin) || margin >= 100) return "";
  const sale = cost / (1 - margin / 100);
  if (!Number.isFinite(sale) || sale <= 0) return "";
  return sale.toFixed(2);
};

const getMarginInputError = (row) => {
  const rawMargin = String(row?.utility_pct ?? "").trim();
  if (!rawMargin) return "";

  const margin = Number(rawMargin);
  if (!Number.isFinite(margin)) return "El margen debe ser un numero valido.";
  if (margin >= 100) return "El margen debe ser menor a 100.";
  return "";
};

const getPresentationFormatError = (row) =>
  row?.product_format_id ? "" : "Selecciona un formato.";

const getPresentationQtyError = (row) =>
  toPositiveNumber(row?.qty_in_base) > 0
    ? ""
    : "La equivalencia debe ser mayor que 0.";

const getPresentationCostError = (row) =>
  toPositiveNumber(row?.cost_price) > 0 ? "" : "El costo debe ser mayor que 0.";

const getTodayDate = () => new Date().toISOString().slice(0, 10);

const buildSearchTokens = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);

const productMatchesAllTokens = (product, tokens) => {
  if (!Array.isArray(tokens) || tokens.length === 0) return true;
  const haystack = `${product?.code || ""} ${product?.description || ""}`.toLowerCase();
  return tokens.every((token) => haystack.includes(token));
};

const normalizeDraftPath = (path) => {
  const raw = String(path || "").trim();
  if (!raw) return "/products/create";
  return raw;
};

const isProductFormPath = (path) => normalizeDraftPath(path).startsWith("/products/create");

const getProductFormDraftStorageKey = (path) => {
  if (!isProductFormPath(path)) return null;
  return `${PRODUCT_FORM_DRAFT_STORAGE_PREFIX}${normalizeDraftPath(path)}`;
};

const createPresentationRow = (localId) => ({
  local_id: localId,
  presentation_id: null,
  product_format_id: "",
  qty_in_base: "",
  cost_price: "",
  price_id: null,
  sale_price: "",
  utility_pct: "",
  last_price_input: "sale",
  has_active_stock: false,
  has_transactions: false,
});

const createBarcodeRow = (localId, seed = {}) => ({
  local_id: localId,
  id: seed.id || null,
  barcode: seed.barcode || "",
  description: seed.description || "",
  source: seed.source || "manual",
  is_primary: Boolean(seed.is_primary),
  is_active: seed.is_active !== false,
});

const toComparableId = (value) => String(value || "");

const isPresentationRowValid = (row) =>
  Boolean(row.product_format_id) &&
  toPositiveNumber(row.qty_in_base) > 0 &&
  toPositiveNumber(row.cost_price) > 0 &&
  toPositiveNumber(row.sale_price) > 0 &&
  !getMarginInputError(row) &&
  !getPresentationPriceError(row);

const hasPresentationData = (row) =>
  [
    row?.product_format_id,
    row?.qty_in_base,
    row?.cost_price,
    row?.sale_price,
    row?.utility_pct,
  ].some((value) => String(value ?? "").trim() !== "");

const isPresentationLocked = (row, productHasActiveStock) =>
  Boolean(productHasActiveStock && (row?.has_active_stock || row?.has_transactions));

const getPresentationSortValue = (row) => {
  const qty = toPositiveNumber(row?.qty_in_base);
  return qty > 0 ? qty : Number.MAX_SAFE_INTEGER;
};

const getPresentationPriceError = (row) => {
  const rawSale = String(row?.sale_price ?? "").trim();
  if (!rawSale) return "Ingresa un precio de venta.";

  const saleValue = Number(rawSale);
  const costValue = toPositiveNumber(row?.cost_price);

  if (!Number.isFinite(saleValue) || saleValue <= 0) {
    return "Ingresa un precio de venta valido.";
  }
  if (costValue > 0 && saleValue < costValue) {
    return "El precio de venta no puede ser menor al costo.";
  }
  return "";
};

const getTrimmedBarcode = (row) => String(row?.barcode || "").trim();

const buildBarcodePayloads = (barcodes) =>
  toArray(barcodes)
    .map((row) => ({
      id: row.id || null,
      barcode: getTrimmedBarcode(row),
      description: String(row.description || "").trim(),
      source: row.source || "manual",
      is_primary: Boolean(row.is_primary),
      is_active: Boolean(row.is_active),
    }))
    .filter((row) => row.barcode);

const getProductFormValidationErrors = ({
  barcodes,
  formatsById,
  general,
  getIntermediatePresentationError,
  presentations,
}) => {
  const errors = [];

  if (!String(general?.description || "").trim()) {
    errors.push("La descripcion del producto es requerida.");
  }
  if (!general?.category_id) {
    errors.push("Selecciona una categoria.");
  }
  if (!general?.brand_id) {
    errors.push("Selecciona una marca.");
  }
  if (!toArray(presentations).length) {
    errors.push("Agrega al menos una presentacion valida.");
  }

  toArray(presentations).forEach((row, index) => {
    const label =
      formatsById.get(toComparableId(row?.product_format_id))?.name ||
      `presentacion ${index + 1}`;
    const prefix = `En ${label}:`;
    const intermediateError = getIntermediatePresentationError(row);

    [
      getPresentationFormatError(row),
      getPresentationQtyError(row),
      getPresentationCostError(row),
      getPresentationPriceError(row),
      getMarginInputError(row),
      intermediateError,
    ]
      .filter(Boolean)
      .forEach((message) => errors.push(`${prefix} ${message}`));
  });

  const barcodeRows = toArray(barcodes);
  const seenBarcodes = new Set();
  let primaryCount = 0;

  barcodeRows.forEach((row, index) => {
    const barcode = getTrimmedBarcode(row);
    if (row?.is_primary) primaryCount += 1;

    if (!barcode) {
      errors.push(`El codigo de barra #${index + 1} esta vacio. Completalo o elimina la fila.`);
      return;
    }

    const comparableBarcode = barcode.toLowerCase();
    if (seenBarcodes.has(comparableBarcode)) {
      errors.push(`El codigo de barra "${barcode}" esta duplicado en el formulario.`);
      return;
    }
    seenBarcodes.add(comparableBarcode);
  });

  if (primaryCount > 1) {
    errors.push("Solo un codigo de barra puede estar marcado como principal.");
  }

  return errors;
};

const BACKEND_FIELD_LABELS = {
  barcode: "Codigo de barra",
  barcodes: "Codigos de barra",
  brand_id: "Marca",
  category_id: "Categoria",
  code: "Codigo",
  cost_price: "Costo",
  detail: "",
  message: "",
  non_field_errors: "",
  presentations: "Presentaciones",
  prices: "Precios",
  product_format_id: "Formato",
  qty_in_base: "Equivalencia",
  sale_price: "Precio de venta",
  utility_pct: "Margen",
};

const formatBackendLabel = (key) => BACKEND_FIELD_LABELS[key] ?? key;

const flattenBackendErrors = (value, path = "") => {
  if (value === null || value === undefined) return [];

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    const message = String(value);
    return path ? [`${path}: ${message}`] : [message];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item, index) => {
      if (
        typeof item === "string" ||
        typeof item === "number" ||
        typeof item === "boolean"
      ) {
        return path ? [`${path}: ${String(item)}`] : [String(item)];
      }

      const nextPath = path ? `${path} #${index + 1}` : `#${index + 1}`;
      return flattenBackendErrors(item, nextPath);
    });
  }

  if (typeof value === "object") {
    return Object.entries(value).flatMap(([key, item]) => {
      const label = formatBackendLabel(key);
      const nextPath = label ? (path ? `${path} > ${label}` : label) : path;
      return flattenBackendErrors(item, nextPath);
    });
  }

  return [];
};

const extractProductSubmitErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;

  const prioritizedMessages = ["detail", "message", "non_field_errors", "code", "presentations"]
    .flatMap((key) => flattenBackendErrors(data?.[key], formatBackendLabel(key)))
    .filter(Boolean);

  const messages = prioritizedMessages.length
    ? prioritizedMessages
    : flattenBackendErrors(data).filter(Boolean);

  if (!messages.length) return error?.message || fallback;
  return messages.slice(0, 4).join(" ");
};

const ensureSavedProductId = (savedProduct, actionLabel) => {
  if (savedProduct?.id) return savedProduct.id;

  const error = new Error(`${actionLabel} no devolvio un producto con id valido.`);
  error.response = {
    data: savedProduct || null,
    status: null,
  };
  throw error;
};

const applyCostToRow = (row, nextCostValue, preserveInputValue = true) => {
  const costValue = preserveInputValue ? nextCostValue : asMoney(nextCostValue);
  const nextRow = { ...row, cost_price: costValue };

  if (row.last_price_input === "margin" && String(row.utility_pct || "").trim() !== "") {
    return {
      ...nextRow,
      sale_price: getSaleFromMargin(costValue, row.utility_pct),
    };
  }

  if (String(row.sale_price || "").trim() !== "") {
    return {
      ...nextRow,
      utility_pct: getMarginFromSale(costValue, row.sale_price),
    };
  }

  return nextRow;
};

const recalcCostsFromReference = (rows, editedLocalId) => {
  const referenceCandidates = rows
    .filter(
      (row) =>
        toPositiveNumber(row.qty_in_base) > 0 && toPositiveNumber(row.cost_price) > 0
    )
    .sort((a, b) => toPositiveNumber(b.qty_in_base) - toPositiveNumber(a.qty_in_base));

  const referenceRow = referenceCandidates[0];
  if (!referenceRow) return rows;

  const referenceQty = toPositiveNumber(referenceRow.qty_in_base);
  const referenceCost = toPositiveNumber(referenceRow.cost_price);
  if (!referenceQty || !referenceCost) return rows;

  const costPerUnit = referenceCost / referenceQty;

  return rows.map((row) => {
    if (row.local_id === editedLocalId || row.local_id === referenceRow.local_id) {
      return row;
    }

    const qty = toPositiveNumber(row.qty_in_base);
    if (!qty) return row;

    const suggestedCost = costPerUnit * qty;
    return applyCostToRow(row, suggestedCost, false);
  });
};

export default function CreateProduct() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isSuperuser, permissions = [] } = useAuth();

  const rowSeqRef = useRef(1);
  const barcodeSeqRef = useRef(1);
  const draftHydratedRef = useRef(false);
  const submittingRef = useRef(false);

  const productId = searchParams.get("product_id");
  const isEditMode = Boolean(productId);
  const draftStorageKey = useMemo(
    () => getProductFormDraftStorageKey(`${location.pathname}${location.search}`),
    [location.pathname, location.search]
  );

  const canManageProducts = hasPermission(permissions, "manage_products", isSuperuser);
  const canManageBarcodes =
    hasPermission(permissions, "manage_product_barcodes", isSuperuser) ||
    canManageProducts;

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [families, setFamilies] = useState([]);
  const [formats, setFormats] = useState([]);

  const [general, setGeneral] = useState({
    code: "",
    description: "",
    category_id: "",
    family_product_id: "",
    brand_id: "",
  });
  const [productIsActive, setProductIsActive] = useState(true);
  const [presentations, setPresentations] = useState([]);
  const [barcodes, setBarcodes] = useState([]);
  const [stockLockContext, setStockLockContext] = useState({
    hasActiveStock: false,
    maxQty: "",
    maxFormatName: "",
  });

  const [loading, setLoading] = useState(false);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);
  const [submitAction, setSubmitAction] = useState("default");
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");
  const [baseSearchOpen, setBaseSearchOpen] = useState(false);
  const [baseSearchTerm, setBaseSearchTerm] = useState("");
  const [baseSearchLoading, setBaseSearchLoading] = useState(false);
  const [baseSearchResults, setBaseSearchResults] = useState([]);
  const [baseSelectingId, setBaseSelectingId] = useState(null);

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  const formatsById = useMemo(
    () => new Map(formats.map((format) => [toComparableId(format.id), format])),
    [formats]
  );

  const validPresentationRows = useMemo(
    () => presentations.filter((row) => isPresentationRowValid(row)),
    [presentations]
  );

  const orderedPresentationRows = useMemo(
    () =>
      [...presentations].sort((left, right) => {
        const qtyDiff = getPresentationSortValue(left) - getPresentationSortValue(right);
        if (qtyDiff !== 0) return qtyDiff;
        return left.local_id - right.local_id;
      }),
    [presentations]
  );

  const orderedValidPresentationRows = useMemo(
    () =>
      [...validPresentationRows].sort(
        (left, right) => toPositiveNumber(left.qty_in_base) - toPositiveNumber(right.qty_in_base)
      ),
    [validPresentationRows]
  );

  const hierarchyRows = useMemo(
    () =>
      orderedValidPresentationRows.map((row) => {
        const format = formatsById.get(toComparableId(row.product_format_id));
        return {
          ...row,
          format_name: format?.name || "Presentacion",
        };
      }),
    [formatsById, orderedValidPresentationRows]
  );

  const getIntermediatePresentationError = useCallback(
    (row) => {
      if (!stockLockContext.hasActiveStock || row?.presentation_id) return "";

      const qtyValue = toPositiveNumber(row?.qty_in_base);
      const maxQtyValue = toPositiveNumber(stockLockContext.maxQty);
      if (!qtyValue || !maxQtyValue || qtyValue < maxQtyValue) return "";

      return (
        "Solo puedes agregar presentaciones intermedias. " +
        `La cantidad debe ser menor a ${formatQtyLabel(stockLockContext.maxQty)} ` +
        `(${stockLockContext.maxFormatName || "Presentacion"}).`
      );
    },
    [stockLockContext]
  );

  const formValidationErrors = useMemo(
    () =>
      getProductFormValidationErrors({
        barcodes,
        formatsById,
        general,
        getIntermediatePresentationError,
        presentations,
      }),
    [barcodes, formatsById, general, getIntermediatePresentationError, presentations]
  );

  const canSubmit = useMemo(() => {
    if (!canManageProducts) return false;
    return formValidationErrors.length === 0;
  }, [canManageProducts, formValidationErrors.length]);

  const getAvailableFormatsForRow = useCallback(
    (rowId) => {
      const selectedInOtherRows = new Set(
        presentations
          .filter((row) => row.local_id !== rowId && row.product_format_id)
          .map((row) => toComparableId(row.product_format_id))
      );

      return formats.filter(
        (format) => !selectedInOtherRows.has(toComparableId(format.id))
      );
    },
    [formats, presentations]
  );

  const applyDraftState = useCallback((draft) => {
    const nextGeneral = draft?.general && typeof draft.general === "object"
      ? {
          code: String(draft.general.code || ""),
          description: String(draft.general.description || ""),
          category_id: draft.general.category_id || "",
          family_product_id: draft.general.family_product_id || "",
          brand_id: draft.general.brand_id || "",
        }
      : {
          code: "",
          description: "",
          category_id: "",
          family_product_id: "",
          brand_id: "",
        };

    const nextRows = toArray(draft?.presentations).map((row, index) => ({
      ...createPresentationRow(index + 1),
      ...row,
      local_id: Number(row?.local_id || index + 1),
    }));
    const nextMaxLocalId = nextRows.reduce(
      (max, row) => Math.max(max, Number(row?.local_id || 0)),
      0
    );

    setGeneral(nextGeneral);
    setProductIsActive(draft?.productIsActive !== false);
    setPresentations(nextRows);
    setBarcodes(toArray(draft?.barcodes).map((row, index) => createBarcodeRow(index + 1, row)));
    setStockLockContext({
      hasActiveStock: Boolean(draft?.stockLockContext?.hasActiveStock),
      maxQty: draft?.stockLockContext?.maxQty || "",
      maxFormatName: draft?.stockLockContext?.maxFormatName || "",
    });
    rowSeqRef.current = nextMaxLocalId + 1;
  }, []);

  
  const loadInitialData = useCallback(async () => {
    draftHydratedRef.current = false;
    setLoadingCatalogs(true);
    try {
      const [categoriesData, brandsData, familiesData, formatsData, productData] =
        await Promise.all([
          categoriesService.list({ active: "true" }),
          brandsService.list({ active: "true" }),
          familiesService.list({ active: "true" }),
          formatsService.list({ active: "true" }),
          isEditMode && productId ? productsService.get(productId) : Promise.resolve(null),
        ]);

      const categoriesList = toArray(categoriesData);
      const brandsList = toArray(brandsData);
      const familiesList = toArray(familiesData);
      const formatsList = toArray(formatsData);

      setCategories(categoriesList);
      setBrands(brandsList);
      setFamilies(familiesList);
      setFormats(formatsList);

      if (draftStorageKey) {
        const rawDraft = safeSessionStorageGetItem(draftStorageKey);
        if (rawDraft) {
          try {
            const parsedDraft = JSON.parse(rawDraft);
            applyDraftState(parsedDraft);
            draftHydratedRef.current = true;
            return;
          } catch {
            safeSessionStorageRemoveItem(draftStorageKey);
          }
        }
      }

      if (productData) {
        setGeneral({
          code: productData?.code || "",
          description: productData?.description || "",
          category_id: productData?.category?.id || "",
          family_product_id: productData?.family_product?.id || "",
          brand_id: productData?.brand?.id || "",
        });
        setProductIsActive(productData?.is_active !== false);
        setBarcodes(toArray(productData?.barcodes).map((row) => createBarcodeRow(barcodeSeqRef.current++, row)));

        const rows = toArray(productData?.presentations)
          .filter((presentation) => presentation?.is_active !== false)
          .sort((a, b) => toPositiveNumber(a?.qty_in_base) - toPositiveNumber(b?.qty_in_base))
          .map((presentation) => {
            const localId = rowSeqRef.current++;
            const prices = toArray(presentation?.prices);
            const activePrice =
              prices.find((price) => price?.is_active !== false) || prices[0] || null;

            const salePrice =
              activePrice?.sale_price !== null && activePrice?.sale_price !== undefined
                ? String(activePrice.sale_price)
                : "";

            const utilityPct =
              activePrice?.utility_pct !== null && activePrice?.utility_pct !== undefined
                ? String(activePrice.utility_pct)
                : getMarginFromSale(presentation?.cost_price, salePrice);

            return {
              local_id: localId,
              presentation_id: presentation?.id || null,
              product_format_id: presentation?.product_format?.id || "",
              qty_in_base:
                presentation?.qty_in_base !== null && presentation?.qty_in_base !== undefined
                  ? String(presentation.qty_in_base)
                  : "",
              cost_price:
                presentation?.cost_price !== null && presentation?.cost_price !== undefined
                  ? String(presentation.cost_price)
                  : "",
              price_id: activePrice?.id || null,
              sale_price: salePrice,
              utility_pct: utilityPct || "",
              last_price_input: salePrice ? "sale" : "margin",
              has_active_stock: Boolean(presentation?.has_active_stock),
              has_transactions: Boolean(presentation?.has_transactions),
            };
          });

        const persistedRows = rows.filter((row) => Boolean(row.presentation_id));
        const hasActiveStock = persistedRows.some((row) => Boolean(row.has_active_stock));
        const maxPersistedRow = [...persistedRows].sort(
          (left, right) => getPresentationSortValue(left) - getPresentationSortValue(right)
        )[persistedRows.length - 1];

        setPresentations(rows);
        setStockLockContext({
          hasActiveStock,
          maxQty: hasActiveStock ? maxPersistedRow?.qty_in_base || "" : "",
          maxFormatName:
            hasActiveStock
              ? formatsList.find(
                  (format) => toComparableId(format.id) === toComparableId(maxPersistedRow?.product_format_id)
                )?.name || "Presentacion"
              : "",
        });
        draftHydratedRef.current = true;
      } else {
        setProductIsActive(true);
        setPresentations([]);
        setBarcodes([]);
        setStockLockContext({
          hasActiveStock: false,
          maxQty: "",
          maxFormatName: "",
        });
        rowSeqRef.current = 1;
        draftHydratedRef.current = true;
      }
    } catch {
      showSnack("No se pudieron cargar los catalogos del producto.", "error");
    } finally {
      setLoadingCatalogs(false);
    }
  }, [applyDraftState, draftStorageKey, isEditMode, productId]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (!draftStorageKey || loadingCatalogs || !draftHydratedRef.current) return;

    safeSessionStorageSetItem(
      draftStorageKey,
      JSON.stringify({
        general,
        productIsActive,
        presentations,
        barcodes,
        stockLockContext,
      })
    );
  }, [barcodes, draftStorageKey, general, loadingCatalogs, presentations, productIsActive, stockLockContext]);

  const handleGeneralChange = (field, value) => {
    setGeneral((prev) => ({ ...prev, [field]: value }));
  };

  const hydrateTemplateFromProduct = useCallback((productData) => {
    const rows = toArray(productData?.presentations)
      .filter((presentation) => presentation?.is_active !== false)
      .sort((a, b) => toPositiveNumber(a?.qty_in_base) - toPositiveNumber(b?.qty_in_base))
      .map((presentation, index) => {
        const prices = toArray(presentation?.prices);
        const activePrice =
          prices.find((price) => price?.is_active !== false) || prices[0] || null;
        const salePrice =
          activePrice?.sale_price !== null && activePrice?.sale_price !== undefined
            ? String(activePrice.sale_price)
            : "";
        const utilityPct =
          activePrice?.utility_pct !== null && activePrice?.utility_pct !== undefined
            ? String(activePrice.utility_pct)
            : getMarginFromSale(presentation?.cost_price, salePrice);

        return {
          ...createPresentationRow(index + 1),
          local_id: index + 1,
          product_format_id: presentation?.product_format?.id || "",
          qty_in_base:
            presentation?.qty_in_base !== null && presentation?.qty_in_base !== undefined
              ? String(presentation.qty_in_base)
              : "",
          cost_price:
            presentation?.cost_price !== null && presentation?.cost_price !== undefined
              ? String(presentation.cost_price)
              : "",
          sale_price: salePrice,
          utility_pct: utilityPct || "",
          last_price_input: salePrice ? "sale" : "margin",
          presentation_id: null,
          price_id: null,
          has_active_stock: false,
          has_transactions: false,
        };
      });

    setGeneral({
      code: "",
      description: productData?.description || "",
      category_id: productData?.category?.id || "",
      family_product_id: productData?.family_product?.id || "",
      brand_id: productData?.brand?.id || "",
    });
    setProductIsActive(true);
    setPresentations(rows);
    setStockLockContext({
      hasActiveStock: false,
      maxQty: "",
      maxFormatName: "",
    });
    rowSeqRef.current = rows.length + 1;
  }, []);

  const resetCreateForm = useCallback(() => {
    setGeneral({
      code: "",
      description: "",
      category_id: "",
      family_product_id: "",
      brand_id: "",
    });
    setProductIsActive(true);
    setPresentations([]);
    setBarcodes([]);
    setStockLockContext({
      hasActiveStock: false,
      maxQty: "",
      maxFormatName: "",
    });
    rowSeqRef.current = 1;
    barcodeSeqRef.current = 1;
    if (draftStorageKey) {
      safeSessionStorageRemoveItem(draftStorageKey);
    }
  }, [draftStorageKey]);

  const isCodeInUse = useCallback(
    async (rawCode) => {
      const normalizedCode = String(rawCode || "").trim().toLowerCase();
      if (!normalizedCode) return false;

      try {
        const data = await productsService.list({ search: normalizedCode });
        const rows = toArray(data);
        return rows.some((row) => {
          if (isEditMode && productId && String(row?.id) === String(productId)) {
            return false;
          }
          return String(row?.code || "").trim().toLowerCase() === normalizedCode;
        });
      } catch {
        return false;
      }
    },
    [isEditMode, productId]
  );

  const handleSelectBaseProduct = useCallback(
    async (baseProduct) => {
      const selectedId = Number(baseProduct?.id);
      if (!Number.isInteger(selectedId) || selectedId <= 0) {
        showSnack("Producto base invalido.", "warning");
        return;
      }

      setBaseSelectingId(selectedId);
      try {
        const detail = await productsService.get(selectedId);
        hydrateTemplateFromProduct(detail);
        setBaseSearchOpen(false);
        setBaseSearchResults([]);
        setBaseSearchTerm("");
        showSnack("Producto base cargado. Completa los datos para crear la variante.", "success");
      } catch {
        showSnack("No se pudo cargar el producto base.", "error");
      } finally {
        setBaseSelectingId(null);
      }
    },
    [hydrateTemplateFromProduct]
  );

  useEffect(() => {
    if (!baseSearchOpen) {
      setBaseSearchLoading(false);
      return undefined;
    }

    const query = String(baseSearchTerm || "").trim();
    if (query.length < 2) {
      setBaseSearchResults([]);
      return undefined;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      setBaseSearchLoading(true);
      try {
        const tokens = buildSearchTokens(query);
        const lookupTerm = tokens.find((token) => token.length >= 2) || query;
        const data = await productsService.list({
          search: lookupTerm,
          active: "true",
          sales_search: "true",
          limit: 30,
        });
        if (!cancelled) {
          const results = toArray(data).filter((row) => productMatchesAllTokens(row, tokens));
          setBaseSearchResults(results);
        }
      } catch {
        if (!cancelled) {
          setBaseSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setBaseSearchLoading(false);
        }
      }
    }, 240);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [baseSearchOpen, baseSearchTerm]);

  const addPresentation = () => {
    const pendingRow = presentations.find((row) => !isPresentationRowValid(row));
    if (pendingRow) {
      showSnack("Completa o elimina la presentacion pendiente antes de agregar otra.", "info");
      return;
    }
    const availableFormats = getAvailableFormatsForRow(null);
    if (!availableFormats.length) {
      showSnack("No hay mas formatos disponibles para agregar.", "info");
      return;
    }
    const localId = rowSeqRef.current++;
    setPresentations((prev) => [...prev, createPresentationRow(localId)]);
  };

  const removePresentation = (localId) => {
    const targetRow = presentations.find((row) => row.local_id === localId);
    if (isPresentationLocked(targetRow, stockLockContext.hasActiveStock)) {
      showSnack("Esta presentacion no puede eliminarse porque tiene stock o transacciones.", "warning");
      return;
    }
    setPresentations((prev) => prev.filter((row) => row.local_id !== localId));
  };

  const addBarcode = () => {
    setBarcodes((prev) => [...prev, createBarcodeRow(barcodeSeqRef.current++)]);
  };

  const removeBarcode = (localId) => {
    setBarcodes((prev) => prev.filter((row) => row.local_id !== localId));
  };

  const updateBarcodeField = (localId, field, value) => {
    setBarcodes((prev) =>
      prev.map((row) => {
        if (row.local_id !== localId) {
          return field === "is_primary" && value ? { ...row, is_primary: false } : row;
        }
        return { ...row, [field]: value };
      })
    );
  };

  const updatePresentationField = (localId, field, value) => {
    setPresentations((prev) => {
      const nextRows = prev.map((row) => {
        if (row.local_id !== localId) return row;

        if (field === "cost_price") {
          return applyCostToRow(
            {
              ...row,
              [field]: value,
            },
            value,
            true
          );
        }

        return {
          ...row,
          [field]: value,
        };
      });

      if (field === "cost_price" || field === "qty_in_base") {
        return recalcCostsFromReference(nextRows, localId);
      }

      return nextRows;
    });
  };

  const updateSalePrice = (localId, salePrice) => {
    setPresentations((prev) =>
      prev.map((row) => {
        if (row.local_id !== localId) return row;
        return {
          ...row,
          sale_price: salePrice,
          utility_pct: getMarginFromSale(row.cost_price, salePrice),
          last_price_input: "sale",
        };
      })
    );
  };

  const updateMarginPct = (localId, marginPct) => {
    setPresentations((prev) =>
      prev.map((row) => {
        if (row.local_id !== localId) return row;
        return {
          ...row,
          utility_pct: marginPct,
          sale_price: getSaleFromMargin(row.cost_price, marginPct),
          last_price_input: "margin",
        };
      })
    );
  };

  const buildProductPayload = () => {
    const payload = {
      description: String(general.description || "").trim(),
      category_id: general.category_id || null,
      family_product_id: general.family_product_id || null,
      brand_id: general.brand_id || null,
      is_active: Boolean(productIsActive),
    };
    const internalCode = String(general.code || "").trim();
    if (internalCode) {
      payload.code = internalCode;
    }
    return payload;
  };

  const buildPresentationsPayload = () => {
    const today = getTodayDate();

    return orderedValidPresentationRows.map((row) => {
      const saleValue = toPositiveNumber(row.sale_price);
      const costValue = toPositiveNumber(row.cost_price);
      const utilityPct = getMarginFromSale(costValue, saleValue);

      const payload = {
        id: row.presentation_id || undefined,
        product_format_id: row.product_format_id,
        qty_in_base: row.qty_in_base,
        cost_price: row.cost_price,
        is_active: true,
        prices: [],
      };

      if (saleValue > 0) {
        payload.prices = [
          {
            id: row.price_id || undefined,
            sale_price: asMoney(saleValue),
            utility_pct: utilityPct || "0.00",
            date_begin: today,
            date_end: "2099-12-31",
            is_active: true,
          },
        ];
      }

      return payload;
    });
  };

  const handleSubmit = async ({ continueCreating = false } = {}) => {
    if (submittingRef.current) {
      console.info("PRODUCT_SUBMIT_START", {
        ignored: true,
        reason: "submit_in_progress",
        productId,
        timestamp: new Date().toISOString(),
      });
      return;
    }

    setSubmitAttempted(true);

    if (!canManageProducts) {
      showSnack("No autorizado para gestionar productos.", "warning");
      return;
    }

    if (!canSubmit) {
      showSnack(formValidationErrors[0] || "Completa los campos requeridos.", "warning");
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    setSubmitAction(
      isEditMode ? "update" : continueCreating ? "create_continue" : "create"
    );

    try {
      const productPayload = buildProductPayload();
      const presentationsPayload = buildPresentationsPayload();
      const barcodePayloads = buildBarcodePayloads(barcodes);

      console.info("PRODUCT_SUBMIT_START", {
        action: isEditMode ? "update" : "create",
        continueCreating,
        productId,
        timestamp: new Date().toISOString(),
      });
      console.info("PRODUCT_SUBMIT_PAYLOAD", {
        barcodes: barcodePayloads,
        product: productPayload,
        presentations: presentationsPayload,
      });

      if (productPayload.code) {
        const duplicateCode = await isCodeInUse(productPayload.code);
        if (duplicateCode) {
          showSnack("El codigo ya existe. Usa un codigo unico.", "warning");
          return;
        }
      }

      if (isEditMode && productId) {
        const savedProduct = await productsService.update(productId, {
          ...productPayload,
          presentations: presentationsPayload,
        });
        console.info("PRODUCT_UPDATE_RESPONSE", savedProduct);

        const targetProductId = ensureSavedProductId(
          savedProduct,
          "La actualizacion del producto"
        );
        await Promise.all(
          barcodePayloads.map((payload) => {
            const { id, ...barcodePayload } = payload;
            return id
              ? productsService.updateBarcode(targetProductId, id, barcodePayload)
              : productsService.createBarcode(targetProductId, barcodePayload);
          })
        );
        showSnack("Producto actualizado correctamente.", "success");
      } else {
        const savedProduct = await productsService.createFull({
          ...productPayload,
          presentations: presentationsPayload,
        });
        console.info("PRODUCT_CREATE_RESPONSE", savedProduct);

        const savedProductId = ensureSavedProductId(
          savedProduct,
          "La creacion del producto"
        );
        await Promise.all(
          barcodePayloads.map((payload) => {
            const { id, ...barcodePayload } = payload;
            return productsService.createBarcode(savedProductId, barcodePayload);
          })
        );

        if (continueCreating) {
          resetCreateForm();
          setSubmitAttempted(false);
          showSnack("Producto creado. El formulario quedo limpio para registrar otro.", "success");
          return;
        }
        showSnack("Producto creado correctamente.", "success");
      }

      if (draftStorageKey) {
        safeSessionStorageRemoveItem(draftStorageKey);
      }

      setSubmitAttempted(false);
      setTimeout(() => navigate("/products"), 250);
    } catch (error) {
      console.error("PRODUCT_SUBMIT_ERROR", {
        message: error?.message || String(error),
        productId,
        stack: error?.stack || "",
      });
      console.error("PRODUCT_SUBMIT_ERROR_RESPONSE", error?.response?.data || null);
      console.error("PRODUCT_SUBMIT_ERROR_STATUS", error?.response?.status || null);

      const message = extractProductSubmitErrorMessage(
        error,
        isEditMode ? "No se pudo actualizar el producto." : "No se pudo crear el producto."
      );
      showSnack(message, "error");
    } finally {
      submittingRef.current = false;
      setLoading(false);
      setSubmitAction("default");
    }
  };

  if (!canManageProducts) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para gestionar productos.
        </Typography>
      </Box>
    );
  }

  const selectedFormatIds = new Set(
    presentations
      .map((row) => row.product_format_id)
      .filter(Boolean)
      .map((value) => toComparableId(value))
  );
  const canAddPresentation = selectedFormatIds.size < formats.length;
  const submitControlsDisabled = loading || loadingCatalogs || submittingRef.current;
  const showFieldValidation = submitAttempted;
  const descriptionError =
    showFieldValidation && !String(general.description || "").trim();
  const categoryError = showFieldValidation && !general.category_id;
  const brandError = showFieldValidation && !general.brand_id;
  const rightCardAccent = theme.palette.secondary?.main || theme.palette.primary.light;
  const getPresentationStatusMeta = (row) => {
    if (stockLockContext.hasActiveStock && row?.has_active_stock && row?.has_transactions) {
      return {
        label: "Con stock e historial",
        color: "warning",
        tooltip: "Para cambiar la cantidad, agrega una presentacion nueva y desactiva esta desde el inventario.",
      };
    }
    if (stockLockContext.hasActiveStock && row?.has_active_stock) {
      return {
        label: "Con stock",
        color: "success",
        tooltip: "Para cambiar la cantidad, agrega una presentacion nueva y desactiva esta desde el inventario.",
      };
    }
    if (stockLockContext.hasActiveStock && row?.has_transactions) {
      return {
        label: "Con historial",
        color: "info",
        tooltip: "Para cambiar la cantidad, agrega una presentacion nueva y desactiva esta desde el inventario.",
      };
    }
    return {
      label: "Editable",
      color: "default",
      tooltip: "Puedes editar formato, equivalencia, costos y precio.",
    };
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "nowrap",
          overflowX: "auto",
        }}
      >
        <MainText
        icon={AddComment}
        title={isEditMode ? "Editar producto" : "Nuevo producto"}
        subtitle={'Llena los campos con la información del producto'}
        />

        <FormControlLabel
          sx={{ my: 0 }}
          control={
            <Switch
              checked={Boolean(productIsActive)}
              onChange={(event) => setProductIsActive(event.target.checked)}
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              {productIsActive ? "Activo" : "Inactivo"}
            </Typography>
          }
        />

        <Divider orientation="vertical" flexItem />

        <Stack direction="row" spacing={1}>
          {isEditMode ? (
            <Button
              variant="contained"
              color="primary"
              disabled={submitControlsDisabled}
              onClick={() => handleSubmit({ continueCreating: false })}
            >
              {loading ? "Guardando..." : "Guardar"}
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                color="primary"
                disabled={submitControlsDisabled}
                onClick={() => handleSubmit({ continueCreating: true })}
              >
                {loading && submitAction === "create_continue"
                  ? "Creando..."
                  : "Crear y continuar"}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={submitControlsDisabled}
                onClick={() => handleSubmit({ continueCreating: false })}
              >
                {loading && submitAction === "create" ? "Creando..." : "Crear"}
              </Button>
            </>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px minmax(0, 1fr)" },
          gap: 2,
        }}
      >
        <Card
          sx={{
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            height: "fit-content",
          }}
        >
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: theme.palette.primary.main }}
              >
                Datos generales
              </Typography>
              {!isEditMode ? (
                <Tooltip title="Buscar producto">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setBaseSearchTerm("");
                        setBaseSearchResults([]);
                        setBaseSearchOpen(true);
                      }}
                      disabled={loading || loadingCatalogs}
                    >
                      <MoreHorizOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </Stack>

            <Stack spacing={1.5}>
              <TextField
                label="Código interno"
                variant="outlined"
                size="small"
                fullWidth
                value={isEditMode ? general.code : "Autogenerado al guardar"}
                disabled
                helperText="Identificador interno del producto. Los códigos de barra se administran abajo."
              />

              <TextField
                label="Descripcion"
                variant="outlined"
                size="small"
                fullWidth
                required
                multiline
                minRows={3}
                value={general.description}
                onChange={(event) => handleGeneralChange("description", event.target.value)}
                error={descriptionError}
                helperText={descriptionError ? "La descripcion del producto es requerida." : " "}
              />

              <FormControl size="small" variant="outlined" fullWidth required error={categoryError}>
                <InputLabel>Categoria</InputLabel>
                <Select
                  label="Categoria"
                  value={general.category_id || ""}
                  onChange={(event) => handleGeneralChange("category_id", event.target.value)}
                >
                  <MenuItem value="">Seleccionar categoria...</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {categoryError ? "La categoria es requerida." : " "}
                </FormHelperText>
              </FormControl>

              <FormControl size="small" variant="outlined" fullWidth required error={brandError}>
                <InputLabel>Marca</InputLabel>
                <Select
                  label="Marca"
                  value={general.brand_id || ""}
                  onChange={(event) => handleGeneralChange("brand_id", event.target.value)}
                >
                  <MenuItem value="">Seleccionar marca...</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {brandError ? "La marca es requerida." : " "}
                </FormHelperText>
              </FormControl>

              <FormControl size="small" variant="outlined" fullWidth>
                <InputLabel>Familia</InputLabel>
                <Select
                  label="Familia"
                  value={general.family_product_id || ""}
                  onChange={(event) =>
                    handleGeneralChange("family_product_id", event.target.value)
                  }
                >
                  <MenuItem value="">(Sin familia)</MenuItem>
                  {families.map((family) => (
                    <MenuItem key={family.id} value={family.id}>
                      {family.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ pt: 0.5 }} />

              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    Códigos de barra
                  </Typography>
                  {canManageBarcodes ? (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addBarcode}
                      disabled={submitControlsDisabled}
                    >
                      Agregar
                    </Button>
                  ) : null}
                </Stack>
                {barcodes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin códigos adicionales. El código principal del producto se mantiene compatible.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {barcodes.map((row, index) => {
                      const barcodeValue = getTrimmedBarcode(row);
                      const duplicateBarcode =
                        barcodeValue &&
                        barcodes.filter(
                          (candidate) =>
                            getTrimmedBarcode(candidate).toLowerCase() ===
                            barcodeValue.toLowerCase()
                        ).length > 1;
                      const emptyBarcodeError = showFieldValidation && !barcodeValue;
                      const duplicateBarcodeError = showFieldValidation && duplicateBarcode;
                      const primaryCount = barcodes.filter((candidate) => candidate?.is_primary).length;
                      const primaryError =
                        showFieldValidation && row?.is_primary && primaryCount > 1;

                      return (
                      <Paper key={row.local_id} variant="outlined" sx={{ p: 1, borderRadius: 1.5 }}>
                        <Stack spacing={1}>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            <TextField
                              label="Código de barra"
                              size="small"
                              value={row.barcode}
                              onChange={(event) => updateBarcodeField(row.local_id, "barcode", event.target.value)}
                              disabled={!canManageBarcodes || submitControlsDisabled}
                              error={Boolean(emptyBarcodeError || duplicateBarcodeError)}
                              helperText={
                                emptyBarcodeError
                                  ? `El codigo de barra #${index + 1} no puede estar vacio.`
                                  : duplicateBarcodeError
                                    ? "Este codigo de barra esta duplicado."
                                    : " "
                              }
                              fullWidth
                            />
                            <TextField
                              label="Descripción"
                              size="small"
                              value={row.description}
                              onChange={(event) => updateBarcodeField(row.local_id, "description", event.target.value)}
                              disabled={!canManageBarcodes || submitControlsDisabled}
                              fullWidth
                            />
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <TextField
                              select
                              label="Fuente"
                              size="small"
                              value={row.source}
                              onChange={(event) => updateBarcodeField(row.local_id, "source", event.target.value)}
                              disabled={!canManageBarcodes || submitControlsDisabled}
                              sx={{ minWidth: 140 }}
                            >
                              <MenuItem value="manual">Manual</MenuItem>
                              <MenuItem value="factory">Fábrica</MenuItem>
                              <MenuItem value="import">Importación</MenuItem>
                              <MenuItem value="legacy">Migrado</MenuItem>
                            </TextField>
                            <FormControlLabel
                              control={<Switch checked={Boolean(row.is_primary)} onChange={(event) => updateBarcodeField(row.local_id, "is_primary", event.target.checked)} disabled={!canManageBarcodes || submitControlsDisabled} />}
                              label="Principal"
                            />
                            <FormControlLabel
                              control={<Switch checked={Boolean(row.is_active)} onChange={(event) => updateBarcodeField(row.local_id, "is_active", event.target.checked)} disabled={!canManageBarcodes || submitControlsDisabled} />}
                              label="Activo"
                            />
                            {canManageBarcodes ? (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeBarcode(row.local_id)}
                                disabled={submitControlsDisabled}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            ) : null}
                          </Stack>
                          {primaryError ? (
                            <Typography variant="caption" color="error">
                              Solo un codigo de barra puede estar marcado como principal.
                            </Typography>
                          ) : null}
                        </Stack>
                      </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Stack>

              <Divider sx={{ pt: 0.5 }} />

              <Stack spacing={1}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: theme.palette.primary.main }}
                >
                  Jerarquia
                </Typography>
                {hierarchyRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Agrega presentaciones validas para ver la secuencia minimo → maximo.
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" alignItems="center">
                    {hierarchyRows.map((row, index) => {
                      const isFirst = index === 0;
                      const isLast = index === hierarchyRows.length - 1;
                      return (
                        <React.Fragment key={`hierarchy-${row.local_id}`}>
                          <Chip
                            label={`${row.format_name} · ${row.qty_in_base}`}
                            color={isFirst ? "success" : isLast ? "info" : "default"}
                            variant={isFirst || isLast ? "filled" : "outlined"}
                            sx={{ fontWeight: 700 }}
                          />
                          {!isLast && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                              →
                            </Typography>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderLeft: `4px solid ${rightCardAccent}` }}>
          <CardContent>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1.5 }}
            >
              Presentaciones
            </Typography>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography variant="body2" color="text.secondary">
                Cada fila define formato, equivalencia, costo y precio de venta.
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addPresentation}
                disabled={!canAddPresentation || submitControlsDisabled}
                color="primary"
              >
                Agregar presentacion
              </Button>
            </Stack>

            {presentations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Agrega al menos una presentacion para continuar.
              </Typography>
            ) : (
              <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 180 }}>Formato</TableCell>
                      <TableCell sx={{ minWidth: 110 }}>Cantidad</TableCell>
                      <TableCell sx={{ minWidth: 110 }}>Costo</TableCell>
                      <TableCell sx={{ minWidth: 110 }}>Margen %</TableCell>
                      <TableCell sx={{ minWidth: 130 }}>Precio venta</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>Estado</TableCell>
                      <TableCell align="center" sx={{ width: 70 }}>Accion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderedPresentationRows.map((row) => {
                      const availableFormats = getAvailableFormatsForRow(row.local_id);
                      const qtyRestrictionError = getIntermediatePresentationError(row);
                      const shouldShowRowValidation = showFieldValidation || hasPresentationData(row);
                      const formatError = shouldShowRowValidation
                        ? getPresentationFormatError(row)
                        : "";
                      const qtyError = shouldShowRowValidation
                        ? qtyRestrictionError || getPresentationQtyError(row)
                        : qtyRestrictionError;
                      const costError = shouldShowRowValidation
                        ? getPresentationCostError(row)
                        : "";
                      const marginError = shouldShowRowValidation ? getMarginInputError(row) : "";
                      const priceError = shouldShowRowValidation
                        ? getPresentationPriceError(row)
                        : "";
                      const locked = isPresentationLocked(row, stockLockContext.hasActiveStock);
                      const statusMeta = getPresentationStatusMeta(row);

                      return (
                        <TableRow key={row.local_id} hover>
                          <TableCell>
                            <FormControl size="small" variant="outlined" fullWidth error={Boolean(formatError)}>
                              <InputLabel>Formato</InputLabel>
                              <Select
                                label="Formato"
                                value={row.product_format_id || ""}
                                disabled={locked || submitControlsDisabled}
                                onChange={(event) =>
                                  updatePresentationField(
                                    row.local_id,
                                    "product_format_id",
                                    event.target.value
                                  )
                                }
                              >
                                <MenuItem value="">Seleccionar...</MenuItem>
                                {availableFormats.map((format) => (
                                  <MenuItem key={format.id} value={format.id}>
                                    {format.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>{formatError || " "}</FormHelperText>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="outlined"
                              size="small"
                              type="number"
                              fullWidth
                              value={row.qty_in_base}
                              disabled={locked || submitControlsDisabled}
                              error={Boolean(qtyError)}
                              helperText={qtyError || " "}
                              onChange={(event) =>
                                updatePresentationField(
                                  row.local_id,
                                  "qty_in_base",
                                  event.target.value
                                )
                              }
                              inputProps={{ min: 0, step: "0.01" }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="outlined"
                              size="small"
                              type="number"
                              fullWidth
                              value={row.cost_price}
                              disabled={submitControlsDisabled}
                              error={Boolean(costError)}
                              helperText={costError || " "}
                              onChange={(event) =>
                                updatePresentationField(
                                  row.local_id,
                                  "cost_price",
                                  event.target.value
                                )
                              }
                              inputProps={{ min: 0, step: "0.01" }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="outlined"
                              size="small"
                              type="number"
                              fullWidth
                              value={row.utility_pct}
                              disabled={submitControlsDisabled}
                              error={Boolean(marginError)}
                              helperText={marginError || " "}
                              onChange={(event) => updateMarginPct(row.local_id, event.target.value)}
                              inputProps={{ min: 0, max: 99.99, step: "0.01" }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="outlined"
                              size="small"
                              type="number"
                              fullWidth
                              value={row.sale_price}
                              disabled={submitControlsDisabled}
                              onChange={(event) => updateSalePrice(row.local_id, event.target.value)}
                              error={Boolean(priceError)}
                              helperText={priceError || " "}
                              inputProps={{ min: toPositiveNumber(row.cost_price) || 0, step: "0.01" }}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={statusMeta.tooltip}>
                              <Chip
                                size="small"
                                color={statusMeta.color}
                                variant={statusMeta.color === "default" ? "outlined" : "filled"}
                                label={statusMeta.label}
                                sx={{ fontWeight: 700 }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            {locked ? (
                              <Tooltip title={statusMeta.tooltip}>
                                <span>
                                  <IconButton size="small" color="warning" disabled>
                                    <LockOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Eliminar presentacion">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => removePresentation(row.local_id)}
                                  disabled={submitControlsDisabled}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={baseSearchOpen && !isEditMode}
        onClose={() => {
          if (baseSelectingId) return;
          setBaseSearchOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Buscar producto base</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.2}>
            <TextField
              label="Buscar producto"
              size="small"
              value={baseSearchTerm}
              onChange={(event) => setBaseSearchTerm(event.target.value)}
              placeholder="Codigo o descripcion"
              autoFocus
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText="Puedes buscar por varios terminos."
            />

            <Paper variant="outlined" sx={{ maxHeight: 340, overflowY: "auto", p: 1 }}>
              {baseSearchTerm.trim().length < 2 ? (
                <Typography variant="body2" color="text.secondary">
                  Escribe al menos 2 caracteres.
                </Typography>
              ) : baseSearchLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Buscando...
                </Typography>
              ) : baseSearchResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sin resultados.
                </Typography>
              ) : (
                <Stack spacing={0.8}>
                  {baseSearchResults.map((product) => (
                    <Stack
                      key={product.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ px: 0.8, py: 0.7, borderRadius: 1, bgcolor: "background.default" }}
                    >
                      <Box sx={{ minWidth: 0, pr: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {product.description || "Producto"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {product.code || "(Sin codigo)"}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleSelectBaseProduct(product)}
                        disabled={Boolean(baseSelectingId)}
                      >
                        {Number(baseSelectingId) === Number(product.id) ? "Cargando..." : "Usar"}
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBaseSearchOpen(false)}
            disabled={Boolean(baseSelectingId)}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}
